import { useEffect, useRef, useCallback, useState } from 'react';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import { WebLinksAddon } from 'xterm-addon-web-links';

export type TerminalMode = 'local' | 'ssh';

export interface SSHConfig {
  host: string;
  port: number;
  username: string;
  password?: string;
  privateKey?: string;
}

export function useTerminal(mode: TerminalMode, sshConfig?: SSHConfig) {
  const termRef = useRef<HTMLDivElement>(null);
  const terminalRef = useRef<Terminal | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const [connected, setConnected] = useState(false);
  const [wsUrl, setWsUrl] = useState('');

  useEffect(() => {
    const wsBase = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/api/ssh`;
    if (mode === 'ssh' && sshConfig) {
      const params = new URLSearchParams({
        host: sshConfig.host,
        port: String(sshConfig.port),
        username: sshConfig.username,
      });
      if (sshConfig.password) params.set('password', sshConfig.password);
      if (sshConfig.privateKey) params.set('privateKey', sshConfig.privateKey);
      setWsUrl(`${wsBase}/terminal/ssh?${params}`);
    } else {
      setWsUrl(`${wsBase}/terminal/local`);
    }
  }, [mode, sshConfig]);

  useEffect(() => {
    if (!termRef.current) return;

    const term = new Terminal({
      cursorBlink: true,
      cursorStyle: 'block',
      fontSize: 13,
      fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
      theme: {
        background: '#0d1117',
        foreground: '#c9d1d9',
        cursor: '#58a6ff',
        selectionBackground: '#264f78',
        black: '#484f58',
        red: '#ff7b72',
        green: '#3fb950',
        yellow: '#d29922',
        blue: '#58a6ff',
        magenta: '#bc8cff',
        cyan: '#39c5cf',
        white: '#b1bac4',
        brightBlack: '#6e7681',
        brightRed: '#ffa198',
        brightGreen: '#56d364',
        brightYellow: '#e3b341',
        brightBlue: '#79c0ff',
        brightMagenta: '#d2a8ff',
        brightCyan: '#56d4dd',
        brightWhite: '#f0f6fc',
      },
      allowTransparency: true,
      convertEol: true,
      cols: 80,
      rows: 24,
    });

    const fitAddon = new FitAddon();
    term.loadAddon(fitAddon);
    term.loadAddon(new WebLinksAddon());
    fitAddonRef.current = fitAddon;

    term.open(termRef.current);
    fitAddon.fit();

    term.write('\x1b[36m╔══════════════════════════════════════════════╗\r\n');
    term.write('║  \x1b[33mOrion Terminal\x1b[36m — Aether Tech AI         ║\r\n');
    term.write('╚══════════════════════════════════════════════╝\x1b[0m\r\n\r\n');
    term.write('Connecting...\r\n');

    terminalRef.current = term;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      term.clear();
      term.focus();
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'data') {
          term.write(atob(msg.data));
        } else if (msg.type === 'connected') {
          term.write(`\r\n\x1b[32m✓ ${msg.message}\x1b[0m\r\n`);
        } else if (msg.type === 'error') {
          term.write(`\r\n\x1b[31m✗ ${msg.message}\x1b[0m\r\n`);
        } else if (msg.type === 'close' || msg.type === 'exit') {
          term.write('\r\n\x1b[33m! Session closed\x1b[0m\r\n');
          setConnected(false);
        }
      } catch {
        term.write(event.data);
      }
    };

    ws.onerror = () => {
      term.write('\r\n\x1b[31m✗ Connection error\x1b[0m\r\n');
      setConnected(false);
    };

    ws.onclose = () => {
      setConnected(false);
    };

    term.onData((data) => {
      if (ws.readyState === WebSocket.OPEN) {
        const msg = JSON.stringify({ type: 'input', data: btoa(data) });
        ws.send(msg);
      }
    });

    const resizeObserver = new ResizeObserver(() => {
      try {
        fitAddon.fit();
      } catch {
        /* */
      }
      if (ws.readyState === WebSocket.OPEN) {
        const dims = fitAddon.proposeDimensions();
        if (dims) {
          ws.send(JSON.stringify({ type: 'resize', cols: dims.cols, rows: dims.rows }));
        }
      }
    });
    if (termRef.current) resizeObserver.observe(termRef.current);

    return () => {
      resizeObserver.disconnect();
      ws.close();
      term.dispose();
    };
  }, [wsUrl]);

  const fitTerminal = useCallback(() => {
    try {
      fitAddonRef.current?.fit();
    } catch {
      /* */
    }
  }, []);

  return { connected, termRef, fitTerminal };
}
